//
//  Policy.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 16, 2026
//  Author: Bob Keathley
//
//  Policy page. Decodes published release notebooks for display only;
//  verification is the engine's job on the bytes received. The publish gate
//  is security-admin or higher (spec 6n).
//
import { Fragment, useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { api, getAssumed, subscribeAssumed } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import { templateLabel, type CatalogueContentType, type MandateTemplate, type Page, type BirthCertSummary, type PolicyAdoption, type PolicyReleaseMeta, type ReleaseNotebook } from '@shared/api/types'
import { atLeast, type Session } from '@shared/auth'

/** Display-only decode of a release's notebook entries (§6n `release_b64`). Verification is the engine's job, on the bytes as received. */
export function decodeReleaseNotebooks(releaseB64: string): ReleaseNotebook[] {
  const bin = atob(releaseB64)
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0))
  const j = JSON.parse(new TextDecoder().decode(bytes)) as { notebooks?: ReleaseNotebook[] }
  return j.notebooks ?? []
}

function ReleaseNotebooks({ seq, token }: { seq: number; token: string }) {
  const [items, setItems] = useState<ReleaseNotebook[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    api<{ release_b64: string }>(`/policy/releases/${seq}`, {}, token).then((r) => {
      if (!alive) return
      if (!isOk(r)) { setErr(r.error.message); return }
      try { setItems(decodeReleaseNotebooks(r.data.release_b64)) } catch { setErr('Could not read this release.') }
    })
    return () => { alive = false }
  }, [seq, token])
  if (err) return <span className="err">{err}</span>
  if (!items) return <span className="hint">Loading…</span>
  if (items.length === 0) return <span className="hint">No notebooks in this release.</span>
  return (
    <ul style={{ margin: 0 }}>
      {items.map((n, i) => (
        <li key={`${n.agent}-${i}`}><span className="mono">{n.agent}</span> — {n.title || '(untitled)'} · <span className="mono">{n.template_name ? `${n.template_name}@${n.template_version}` : '—'}</span></li>
      ))}
    </ul>
  )
}

/** Notebooks the next release will carry (§6n amended, SIG-147): every born Notebook, console or Mac, template or
 * intake; entries are keyed by Notebook, so a newer birth for an agent does not hide an older one. The server has no
 * preview route, so this is derived from the birth list; the published release below is the record. */
export function nextReleaseNotebooks(certs: BirthCertSummary[]): BirthCertSummary[] {
  return certs.filter((c) => c.status === 'born' || c.status === 'redeemed')
}

function NextReleaseNotebooks({ token }: { token: string }) {
  const [items, setItems] = useState<BirthCertSummary[] | 'unavailable' | null>(null)
  useEffect(() => {
    let alive = true
    api<Page<BirthCertSummary>>('/notebooks/birth-certs', {}, token).then((r) => { if (alive) setItems(isOk(r) ? nextReleaseNotebooks(r.data.items) : 'unavailable') })
    return () => { alive = false }
  }, [token])
  return (
    <>
      <h3>Notebooks in the next release</h3>
      <p className="hint">Will be included when you publish. The published release below is the record.</p>
      {items === null && <p className="hint">Loading…</p>}
      {items === 'unavailable' && <p className="hint">Couldn't load your Notebooks right now.</p>}
      {Array.isArray(items) && items.length === 0 && <p className="hint">No Notebooks yet. <a href="#/birth">Birth a Notebook ›</a></p>}
      {Array.isArray(items) && items.length > 0 && (
        <ul style={{ margin: 0 }}>
          {items.map((c) => (
            <li key={c.id}><span className="mono">{c.agent}</span> — {c.title || '(untitled)'} · <span className="mono">{c.template ? `${c.template.name}@${c.cert?.template_version ?? c.template.version ?? '—'}` : c.source === 'import' ? 'imported' : 'intake answers'}</span> {c.status === 'born' ? <span className="pill ok">born in console</span> : <span className="pill">redeemed on a Mac</span>}</li>
          ))}
        </ul>
      )}
    </>
  )
}

/** Hook-set display (§6n): version, the events it hooks (`mandate.hooks[].event`), and a "default" label on the
 * platform default v1 (`trxy/hooks/claude-code-v1`). v2 (adds `record` target events) is adopted like any hook set. */
export function hookEvents(t: MandateTemplate): string[] {
  const hooks = (t.mandate as { hooks?: { event?: string }[] }).hooks ?? []
  return [...new Set(hooks.map((h) => h.event).filter((e): e is string => !!e))]
}
function HookSetInfo({ t }: { t: MandateTemplate }) {
  const ev = hookEvents(t)
  return (
    <>
      {t.source === 'platform' && t.name.endsWith('-v1') && <> <span className="pill">default</span></>}
      <div className="hint">{ev.length ? `events: ${ev.join(', ')}` : 'no events listed'}</div>
    </>
  )
}

const when = (s: number | null) => (s ? new Date(s * 1000).toLocaleString() : '—')

/** §6n publish gate: security-admin+ only. Hidden for others; disabled with a reason in an assumed read session. */
export function publishState(role: string | undefined, assumed: { mode: 'read' | 'write' } | null): { visible: boolean; reason: string | null } {
  if (!atLeast(role, 'security-admin')) return { visible: false, reason: null }
  if (assumed?.mode === 'read') return { visible: true, reason: 'This is an assumed read-only session; it cannot publish.' }
  return { visible: true, reason: null }
}

export default function Policy({ session }: { session: Session }) {
  const assumed = useSyncExternalStore(subscribeAssumed, getAssumed, getAssumed)
  const canWrite = atLeast(session.user.role, 'security-admin')
  const publish = publishState(session.user.role, assumed)
  const [adoptions, setAdoptions] = useState<PolicyAdoption[] | null>(null)
  const [catalogue, setCatalogue] = useState<Record<string, MandateTemplate[]>>({})
  const [releases, setReleases] = useState<PolicyReleaseMeta[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [openSeq, setOpenSeq] = useState<number | null>(null)

  const load = useCallback(async () => {
    const types: CatalogueContentType[] = ['phases', 'hook_set']
    const [a, r, ...c] = await Promise.all([
      api<{ items: PolicyAdoption[] }>('/policy/adoptions', {}, session.token),
      api<Page<PolicyReleaseMeta>>('/policy/releases', {}, session.token),
      ...types.map((t) => api<Page<MandateTemplate>>(`/mandates/templates?content_type=${t}`, {}, session.token)),
    ])
    if (isOk(a)) setAdoptions(a.data.items); else { setAdoptions([]); setError(a.error.message) }
    if (isOk(r)) setReleases(r.data.items); else { setReleases([]); setError(r.error.message) }
    const cat: Record<string, MandateTemplate[]> = {}
    c.forEach((x, i) => { cat[types[i]] = isOk(x) ? x.data.items.filter((t) => !t.provenance.retired_at) : [] })
    setCatalogue(cat)
  }, [session.token])
  useEffect(() => { load() }, [load])

  const fail = (e: { code: string; message: string }) => setError(e.code === 'assumed_read_only' ? 'This assumed session is read-only.' : e.code === 'kek_unavailable' ? 'The tenant signing key is not configured. Nothing was stored.' : e.message)
  async function adopt(t: MandateTemplate) {
    setError(null); setNotice(null)
    const r = await api<PolicyAdoption>('/policy/adopt', { method: 'POST', body: JSON.stringify({ template_id: t.id }) }, session.token)
    if (!isOk(r)) fail(r.error); else { setNotice('Adopted. This changes the next release only; past releases are unchanged.'); load() }
  }
  async function unadopt(a: PolicyAdoption) {
    setError(null); setNotice(null)
    const r = await api<unknown>(`/policy/adopt/${a.id}`, { method: 'DELETE' }, session.token)
    if (!isOk(r)) fail(r.error); else { setNotice('Un-adopted. This changes the next release only.'); load() }
  }
  async function doPublish() {
    if (!confirm('Publish a signed release of the current policy? Releases are append-only and cannot be edited or deleted.')) return
    setBusy(true); setError(null); setNotice(null)
    const r = await api<unknown>('/policy/releases', { method: 'POST', body: JSON.stringify({}) }, session.token)
    setBusy(false)
    if (!isOk(r)) fail(r.error); else { setNotice('Release published.'); load() }
  }

  const all = Object.values(catalogue).flat()
  const nameOf = (a: PolicyAdoption) => { const t = all.find((x) => x.id === a.template_id); return t ? templateLabel(t) : a.template_id.slice(0, 8) }
  const adoptedIds = new Set((adoptions ?? []).map((a) => a.template_id))
  const section = (type: CatalogueContentType, heading: string) => (
    <>
      <h3>{heading}</h3>
      <table>
        <thead><tr><th>Title</th><th>Source</th><th>Version</th><th></th></tr></thead>
        <tbody>
          {(catalogue[type] ?? []).length === 0 && <tr><td colSpan={4} className="hint">None available in the catalogue.</td></tr>}
          {(catalogue[type] ?? []).map((t) => (
            <tr key={t.id}>
              <td title={t.description ?? undefined}>{templateLabel(t)}</td>
              <td className="hint">{t.source}</td>
              <td className="mono">{t.version}{type === 'hook_set' && <HookSetInfo t={t} />}</td>
              <td style={{ textAlign: 'right' }}>
                {adoptedIds.has(t.id)
                  ? <span className="pill ok">adopted</span>
                  : canWrite && <button className="btn sm" onClick={() => adopt(t)}>Adopt</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )

  return (
    <>
      <h2>Policy</h2>
      <p className="hint">Machines receive these as signed releases. Adopting or un-adopting changes the <b>next</b> release only.</p>
      {error && <p className="err" role="alert">{error}</p>}
      {notice && <p className="hint" role="status">{notice}</p>}

      <h2>Adopted</h2>
      <table>
        <thead><tr><th>Title</th><th>Version</th><th>Adopted</th><th></th></tr></thead>
        <tbody>
          {adoptions === null && <tr><td colSpan={4} className="hint">Loading…</td></tr>}
          {adoptions && adoptions.length === 0 && <tr><td colSpan={4} className="hint">Nothing adopted. Releases use the platform default hook set and no phase sets.</td></tr>}
          {adoptions?.map((a) => (
            <tr key={a.id}>
              <td>{nameOf(a)}</td>
              <td className="mono">{a.version}</td>
              <td className="hint">{when(a.adopted_at)}</td>
              <td style={{ textAlign: 'right' }}>{canWrite && <button className="btn danger sm" onClick={() => unadopt(a)}>Un-adopt</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {section('phases', 'Phase sets in the catalogue')}
      {section('hook_set', 'Hook sets in the catalogue')}

      <h2>Releases</h2>
      <p className="hint">Folders bound as <a href="#/projects">Projects</a> get their own signed release chain, published from each project's page. The tenant-wide releases below are for Macs not bound to a project.</p>
      <NextReleaseNotebooks token={session.token} />
      {publish.visible && (
        <p>
          <button className="btn" onClick={doPublish} disabled={busy || !!publish.reason} title={publish.reason ?? undefined}>{busy ? 'Publishing…' : 'Publish release'}</button>
          {publish.reason && <span className="hint"> {publish.reason}</span>}
        </p>
      )}
      <table>
        <thead><tr><th>Seq</th><th>Issued by</th><th>Issued</th><th>Notebooks</th><th>Hash</th></tr></thead>
        <tbody>
          {releases === null && <tr><td colSpan={5} className="hint">Loading…</td></tr>}
          {releases && releases.length === 0 && <tr><td colSpan={5} className="hint">No releases published.</td></tr>}
          {releases?.map((r) => (
            <Fragment key={r.id}>
            <tr>
              <td className="mono">{r.seq}</td>
              <td className="mono hint">{r.issued_by.slice(0, 8)}</td>
              <td className="hint">{when(r.issued_at)}</td>
              <td><button className="btn ghost sm" aria-expanded={openSeq === r.seq} onClick={() => setOpenSeq(openSeq === r.seq ? null : r.seq)}>{r.notebook_count}</button></td>
              <td className="mono hint" title={r.hash}>{r.hash.slice(0, 12)}…</td>
            </tr>
            {openSeq === r.seq && <tr><td colSpan={5}><ReleaseNotebooks seq={r.seq} token={session.token} /></td></tr>}
            </Fragment>
          ))}
        </tbody>
      </table>
    </>
  )
}
